import { useState, useEffect, KeyboardEventHandler } from 'react';
import { SelectProps } from '@mui/material/Select';
import { useStore } from '@ui/hooks/useStore';
import useKeyDown from '@ui/hooks/useKeyDown';
import { ICloseEvent } from '@core/interfaces/ICloseEvent';
import { IState } from './IState';
import { langs } from '@lang/enums/langs';
import { lang } from '@lang/interfaces/lang';

const useSettings = (onClose: ICloseEvent['onClose']) => {
    const { user, lang, app } = useStore();
    const key = useKeyDown();
    const { lang: language } = lang;

    const initState: IState = {
        timezone: 0,
        language,
    };
    const [state, setState] = useState(initState);

    const localTimezone = new Date().getTimezoneOffset() * -1;
    const values = {
        localTimezone,
    };

    const handleTimezoneChange: SelectProps<number>['onChange'] = (e) => {
        const timezone = +e.target.value;
        setState((prev) => ({ ...prev, timezone }));
    };
    const handleLanguageChange: SelectProps<string>['onChange'] = (e) => {
        const _lang = e.target.value;
        const language = langs[_lang as lang];
        if (_lang === language) setState((prev) => ({ ...prev, language }));
    };
    const handleSaveClick = () => {
        user.resetServerError();
        app.resetDemoModeError();
        if (!app.demoMode) {
            const { timezone, language } = state;
            const settings = { timezone };
            user.changeSettings(settings);
            lang.setLanguage(language);
        } else app.setDemoModeError();
    };
    const handleInputClick = () => {
        user.resetServerError();
        app.resetDemoModeError();
    };
    const handleKey = (key: string) => {
        switch (key) {
            case 'Escape':
                onClose();
                break;
            case 'Enter':
                handleSaveClick();
                break;
        }
    };
    const handleBtnKeyDown: KeyboardEventHandler<HTMLElement> = (e) => {
        const { key } = e;
        if (key === 'Enter') {
            e.stopPropagation();
            e.preventDefault();
            const target = e.target as HTMLElement;
            target.click();
        }
    };
    const handleSelectKeyDown: KeyboardEventHandler<HTMLElement> = (e) => {
        const { key } = e;
        if (key === 'Enter') {
            e.stopPropagation();
            e.preventDefault();
        }
    };
    const handlers = {
        handleTimezoneChange,
        handleSaveClick,
        handleInputClick,
        handleLanguageChange,
        handleBtnKeyDown,
        handleSelectKeyDown,
    };

    useEffect(() => {
        user.resetServerError();
        app.resetDemoModeError();
    }, []);
    useEffect(() => {
        const { timezone } = user.settings;
        if (timezone !== undefined) {
            setState((prev) => ({ ...prev, timezone }));
        }
    }, [user.settings]);
    useEffect(() => {
        if (user.changeSettingsSuccess) {
            onClose();
            user.resetChangeSettingsSuccess();
        }
    }, [user.changeSettingsSuccess]);
    useEffect(() => {
        handleKey(key);
    }, [key]);

    return {
        user,
        lang,
        app,
        state,
        values,
        handlers,
    };
};

export default useSettings;
